import { jsx as _jsx } from "react/jsx-runtime";
import { createRouter, createRootRoute, createRoute, Outlet, redirect, } from '@tanstack/react-router';
import { isAuthenticated, getStoredUser } from '@/lib/auth';
import { ROUTES } from '@/lib/constants';
import { AREA_ROLES, resolveRoleAccess } from './role-access';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AppLayout } from '@/layouts/AppLayout';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { OnboardingPage } from '@/features/onboarding/pages/OnboardingPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { NotificationsPage } from '@/features/notifications/pages/NotificationsPage';
import { StudentDashboardPage } from '@/features/student-dashboard/pages/StudentDashboardPage';
import { DirectorHomePage } from '@/features/director-home/pages/DirectorHomePage';
import { ProfessorHomePage } from '@/features/professor-home/pages/ProfessorHomePage';
import { StudentSchedulePage } from '@/features/student-schedule/pages/StudentSchedulePage';
import { StudentMaterialsPage } from '@/features/student-materials/pages/StudentMaterialsPage';
import { StudentPaymentsPage } from '@/features/student-payments/pages/StudentPaymentsPage';
import { StudentRequestsPage } from '@/features/student-requests/pages/StudentRequestsPage';
import { ReportIncidentPage } from '@/features/support/pages/ReportIncidentPage';
import { SupportInboxPage } from '@/features/support/pages/SupportInboxPage';
import { StudentProfilePage } from '@/features/student-profile/pages/StudentProfilePage';
import { StudentAttendancePage } from '@/features/student-attendance/pages/StudentAttendancePage';
import { StudentsPage } from '@/features/director-students/pages/StudentsPage';
import { DirectorStudentDetailPage } from '@/features/director-students/pages/DirectorStudentDetailPage';
import { PaymentsPage } from '@/features/director-payments/pages/PaymentsPage';
import { RequestsPage } from '@/features/director-requests/pages/RequestsPage';
import { AuditPage } from '@/features/director-audit/pages/AuditPage';
import { ApprovedStudentsPage } from '@/features/director-approved/pages/ApprovedStudentsPage';
import { ProfessorsPage } from '@/features/director-professors/pages/ProfessorsPage';
import { AnnouncementsPage } from '@/features/director-announcements/pages/AnnouncementsPage';
import { ProspectsPage } from '@/features/director-prospects/pages/ProspectsPage';
import { BlogPage } from '@/features/blog/pages/BlogPage';
import { MaterialsAdminPage } from '@/features/director-materials/pages/MaterialsAdminPage';
import { RecordingsAdminPage } from '@/features/director-recordings/pages/RecordingsAdminPage';
import { StudentRecordingsPage } from '@/features/student-recordings/pages/StudentRecordingsPage';
import { StudentTestsPage } from '@/features/student-tests/pages/StudentTestsPage';
import { SchedulesPage } from '@/features/director-schedules/pages/SchedulesPage';
import { ProfessorClassesPage } from '@/features/professor-classes/pages/ProfessorClassesPage';
import { ProfessorMaterialsPage } from '@/features/professor-materials/pages/ProfessorMaterialsPage';
import { CodifiedGuidePage } from '@/features/student-codified-guide/pages/CodifiedGuidePage';
import { PenalCodePage } from '@/features/student-penal-code/pages/PenalCodePage';
import { ConstitutionPage } from '@/features/student-constitution/pages/ConstitutionPage';
import { ProfessorStudentsPage } from '@/features/professor-students/pages/ProfessorStudentsPage';
import { ProfessorPaymentsPage } from '@/features/professor-payments/pages/ProfessorPaymentsPage';
// ── Role guards ───────────────────────────────────────────────────────────────
// Authorization is enforced authoritatively in the backend (RolesGuard + RLS),
// but the panel must NOT render another role's shell/pages either: a student
// must never reach /director/* by editing the URL. These pathless "area" routes
// gate each section by role and bounce the wrong role to its own home.
// The role matrix lives in ./role-access (shared with its unit tests).
/**
 * beforeLoad factory: only the listed roles may enter. Anyone else is redirected
 * to their own home (or login if not authenticated). Mirrors the role mapping in
 * layouts/config/navigation.ts so the menu and the guards never diverge.
 */
function requireRole(...allowed) {
    return () => {
        const access = resolveRoleAccess(getStoredUser(), allowed);
        if (!access.allow) {
            throw redirect({ to: access.redirectTo });
        }
    };
}
// ── Root ─────────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute({
    component: () => _jsx(Outlet, {}),
});
// ── Auth routes ───────────────────────────────────────────────────────────────
const authLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'auth-layout',
    component: AuthLayout,
    beforeLoad: () => {
        if (isAuthenticated()) {
            const user = getStoredUser();
            if (user?.mustChangePassword) {
                throw redirect({ to: ROUTES.CHANGE_PASSWORD });
            }
            throw redirect({ to: ROUTES.DASHBOARD });
        }
    },
});
const loginRoute = createRoute({
    getParentRoute: () => authLayoutRoute,
    path: ROUTES.LOGIN,
    component: LoginPage,
});
const changePasswordLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'change-password-layout',
    component: AuthLayout,
    beforeLoad: () => {
        if (!isAuthenticated()) {
            throw redirect({ to: ROUTES.LOGIN });
        }
    },
});
const changePasswordRoute = createRoute({
    getParentRoute: () => changePasswordLayoutRoute,
    path: ROUTES.CHANGE_PASSWORD,
    component: ChangePasswordPage,
});
// ── Public onboarding (invitation accept) ────────────────────────────────────
const onboardingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: ROUTES.ONBOARDING,
    component: OnboardingPage,
});
// Public password reset (link emailed by the director). No auth required.
const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: ROUTES.RESET_PASSWORD,
    component: ResetPasswordPage,
});
// ── App routes ────────────────────────────────────────────────────────────────
const appLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'app-layout',
    component: AppLayout,
    beforeLoad: () => {
        if (!isAuthenticated()) {
            throw redirect({ to: ROUTES.LOGIN });
        }
        const user = getStoredUser();
        if (user?.mustChangePassword) {
            throw redirect({ to: ROUTES.CHANGE_PASSWORD });
        }
    },
});
const dashboardRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: ROUTES.DASHBOARD,
    component: () => (_jsx("div", { className: "text-gray-700 dark:text-gray-300", children: "Panel \u2014 selecciona una secci\u00F3n del men\u00FA" })),
});
// ── Role areas (pathless layout routes that gate by role) ─────────────────────
const directorAreaRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: 'director-area',
    component: () => _jsx(Outlet, {}),
    beforeLoad: requireRole(...AREA_ROLES.director),
});
const professorAreaRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: 'professor-area',
    component: () => _jsx(Outlet, {}),
    beforeLoad: requireRole(...AREA_ROLES.professor),
});
// Shared by director + professor: blog and the "Mis clases" page the director
// uses under their dual role (ADR-009 — see navigation.ts).
const staffAreaRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: 'staff-area',
    component: () => _jsx(Outlet, {}),
    beforeLoad: requireRole(...AREA_ROLES.staff),
});
const studentAreaRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: 'student-area',
    component: () => _jsx(Outlet, {}),
    beforeLoad: requireRole(...AREA_ROLES.student),
});
const supportAreaRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: 'support-area',
    component: () => _jsx(Outlet, {}),
    beforeLoad: requireRole(...AREA_ROLES.support),
});
// Director routes
const directorHomeRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_HOME,
    component: DirectorHomePage,
});
const directorStudentsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_STUDENTS,
    component: StudentsPage,
});
const directorProfessorsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_PROFESSORS,
    component: ProfessorsPage,
});
const directorProspectsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_PROSPECTS,
    component: ProspectsPage,
});
const directorPaymentsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_PAYMENTS,
    component: PaymentsPage,
});
const directorSchedulesRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_SCHEDULES,
    component: SchedulesPage,
});
const directorMaterialsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_MATERIALS,
    component: MaterialsAdminPage,
});
const directorRecordingsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_RECORDINGS,
    component: RecordingsAdminPage,
});
const directorCodifiedGuideRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_CODIFIED_GUIDE,
    component: CodifiedGuidePage,
});
const directorPenalCodeRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_PENAL_CODE,
    component: PenalCodePage,
});
const directorConstitutionRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_CONSTITUTION,
    component: ConstitutionPage,
});
const directorAnnouncementsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_ANNOUNCEMENTS,
    component: AnnouncementsPage,
});
const directorApprovedRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_APPROVED,
    component: ApprovedStudentsPage,
});
const directorRequestsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_REQUESTS,
    component: RequestsPage,
});
const directorAuditRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_AUDIT,
    component: AuditPage,
});
const directorNotificationsRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_NOTIFICATIONS,
    component: NotificationsPage,
});
const directorStudentDetailRoute = createRoute({
    getParentRoute: () => directorAreaRoute,
    path: ROUTES.DIRECTOR_STUDENT_DETAIL,
    component: DirectorStudentDetailPage,
});
// Professor routes
const professorHomeRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_HOME,
    component: ProfessorHomePage,
});
const professorStudentsRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_STUDENTS,
    component: ProfessorStudentsPage,
});
const professorMaterialsRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_MATERIALS,
    component: ProfessorMaterialsPage,
});
const professorPaymentsRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_PAYMENTS,
    component: ProfessorPaymentsPage,
});
const professorCodifiedGuideRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_CODIFIED_GUIDE,
    component: CodifiedGuidePage,
});
const professorPenalCodeRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_PENAL_CODE,
    component: PenalCodePage,
});
const professorConstitutionRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_CONSTITUTION,
    component: ConstitutionPage,
});
const professorNotificationsRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_NOTIFICATIONS,
    component: NotificationsPage,
});
// Shared staff routes (director + professor)
const professorClassesRoute = createRoute({
    getParentRoute: () => staffAreaRoute,
    path: ROUTES.PROFESSOR_CLASSES,
    component: ProfessorClassesPage,
    // Deep-link desde notificaciones de recordatorio: ?classId= abre el modal de esa clase.
    validateSearch: (search) => typeof search.classId === 'string' && search.classId ? { classId: search.classId } : {},
});
const directorBlogRoute = createRoute({
    getParentRoute: () => staffAreaRoute,
    path: ROUTES.DIRECTOR_BLOG,
    component: BlogPage,
});
const professorBlogRoute = createRoute({
    getParentRoute: () => staffAreaRoute,
    path: ROUTES.PROFESSOR_BLOG,
    component: BlogPage,
});
// Student routes
const studentDashboardRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_DASHBOARD,
    component: StudentDashboardPage,
});
const studentScheduleRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_SCHEDULE,
    component: StudentSchedulePage,
});
const studentMaterialsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_MATERIALS,
    component: StudentMaterialsPage,
});
const studentRecordingsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_RECORDINGS,
    component: StudentRecordingsPage,
});
const studentTestsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_TESTS,
    component: StudentTestsPage,
});
const studentAttendanceRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_ATTENDANCE,
    component: StudentAttendancePage,
});
const studentPaymentsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_PAYMENTS,
    component: StudentPaymentsPage,
});
const studentRequestsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_REQUESTS,
    component: StudentRequestsPage,
});
const studentNotificationsRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_NOTIFICATIONS,
    component: NotificationsPage,
});
const studentProfileRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_PROFILE,
    component: StudentProfilePage,
});
const studentCodifiedGuideRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_CODIFIED_GUIDE,
    component: CodifiedGuidePage,
});
const studentPenalCodeRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_PENAL_CODE,
    component: PenalCodePage,
});
const studentConstitutionRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_CONSTITUTION,
    component: ConstitutionPage,
});
const studentSupportRoute = createRoute({
    getParentRoute: () => studentAreaRoute,
    path: ROUTES.STUDENT_SUPPORT,
    component: ReportIncidentPage,
});
const professorSupportRoute = createRoute({
    getParentRoute: () => professorAreaRoute,
    path: ROUTES.PROFESSOR_SUPPORT,
    component: ReportIncidentPage,
});
const supportInboxRoute = createRoute({
    getParentRoute: () => supportAreaRoute,
    path: ROUTES.SUPPORT_INBOX,
    component: SupportInboxPage,
});
// ── Route tree ────────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
    authLayoutRoute.addChildren([loginRoute]),
    changePasswordLayoutRoute.addChildren([changePasswordRoute]),
    onboardingRoute,
    resetPasswordRoute,
    appLayoutRoute.addChildren([
        dashboardRoute,
        // Director (role=director only)
        directorAreaRoute.addChildren([
            directorHomeRoute,
            directorStudentsRoute,
            directorStudentDetailRoute,
            directorProfessorsRoute,
            directorProspectsRoute,
            directorPaymentsRoute,
            directorSchedulesRoute,
            directorMaterialsRoute,
            directorRecordingsRoute,
            directorAnnouncementsRoute,
            directorApprovedRoute,
            directorRequestsRoute,
            directorAuditRoute,
            directorNotificationsRoute,
            directorCodifiedGuideRoute,
            directorPenalCodeRoute,
            directorConstitutionRoute,
        ]),
        // Professor (role=professor only)
        professorAreaRoute.addChildren([
            professorHomeRoute,
            professorStudentsRoute,
            professorMaterialsRoute,
            professorPaymentsRoute,
            professorCodifiedGuideRoute,
            professorPenalCodeRoute,
            professorConstitutionRoute,
            professorSupportRoute,
            professorNotificationsRoute,
        ]),
        // Shared staff (director + professor)
        staffAreaRoute.addChildren([
            professorClassesRoute,
            directorBlogRoute,
            professorBlogRoute,
        ]),
        // Student (role=student only)
        studentAreaRoute.addChildren([
            studentDashboardRoute,
            studentScheduleRoute,
            studentMaterialsRoute,
            studentRecordingsRoute,
            studentTestsRoute,
            studentAttendanceRoute,
            studentPaymentsRoute,
            studentRequestsRoute,
            studentNotificationsRoute,
            studentProfileRoute,
            studentCodifiedGuideRoute,
            studentPenalCodeRoute,
            studentConstitutionRoute,
            studentSupportRoute,
        ]),
        // Support console (role=support only)
        supportAreaRoute.addChildren([supportInboxRoute]),
    ]),
]);
export const router = createRouter({ routeTree });
//# sourceMappingURL=router.js.map